/**
 * My sessions — the Session app landing screen.
 * Facilitators see their sessions with a host entry; participants see the
 * sessions they're in. One CTA: start a session.
 */

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Radio } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { SESSION_CREATE_BALL, SESSION_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';
import type { PrioritizationSession } from '@/types/prioritization/session';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, PrimaryButton, StatusChip } from '../components/primitives';
import { sessionAppToolName } from './sessionAppActions';
import { SESSION_STATE_LABELS } from '@/lib/sessions/vocabulary';

function stateChip(session: PrioritizationSession) {
  switch (session.state) {
    case 'DRAFT':
      return <StatusChip label={SESSION_STATE_LABELS.DRAFT} tone="neutral" />;
    case 'CRITERIA_FINALIZED':
      return <StatusChip label={SESSION_STATE_LABELS.CRITERIA_FINALIZED} tone="mustard" />;
    case 'RUN_COMPLETE':
      return <StatusChip label={SESSION_STATE_LABELS.RUN_COMPLETE} tone="green" />;
    case 'PUBLISHED':
      return <StatusChip label={SESSION_STATE_LABELS.PUBLISHED} tone="green" />;
    default:
      return <StatusChip label={String(session.state)} tone="neutral" />;
  }
}

export default function MySessionsScreen() {
  const navigate = useNavigate();
  const app = getAppDefinition('session')!;
  const userId = useUserStore((s) => s.userId);
  // New session leads to a form whose insert takes SESSION_RUN (migration 754).
  const mayCreateSession = useCreateRights().session;
  const sessions = useSessionStore((s) => s.sessions);
  const fetchFromSupabase = useSessionStore((s) => s.fetchFromSupabase);

  useEffect(() => {
    void fetchFromSupabase();
  }, [fetchFromSupabase]);

  const mine = useMemo(() => {
    const relevant = sessions.filter(
      (s) =>
        s.intent === 'rank_priorities' &&
        (s.facilitatorId === userId || (s.participantIds ?? []).includes(userId ?? ''))
    );
    return [...relevant].sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
    );
  }, [sessions, userId]);

  return (
    <AppShell
      app={app}
      actions={
        mayCreateSession ? (
          <PrimaryButton onClick={() => navigate('/apps/session/new')}>
            <Plus className="h-4 w-4" /> New session
          </PrimaryButton>
        ) : undefined
      }
    >
      <ScreenHeading
        title="Your sessions"
        subtitle="Set one up, share the link, and everyone weighs in from their own device."
      />
      {mine.length === 0 ? (
        <EmptyState
          title="No sessions yet"
          hint={mayCreateSession ? "Start one — you'll have a shareable link in under a minute." : SESSION_CREATE_LINE}
          action={
            mayCreateSession ? (
              <PrimaryButton onClick={() => navigate('/apps/session/new')}>
                <Plus className="h-4 w-4" /> Start a session
              </PrimaryButton>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {mine.map((session) => {
            const isFacilitator = session.facilitatorId === userId;
            const target = isFacilitator
              ? `/apps/session/${session.id}/host`
              : `/apps/session/${session.id}`;
            return (
              <AppCard key={session.id} onClick={() => navigate(target)}>
                <div className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                      {session.name}
                    </p>
                    <p className="mt-0.5 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                      {sessionAppToolName(session.selectedToolId)}
                      {isFacilitator ? ' · You host' : ' · You participate'}
                    </p>
                  </div>
                  {session.state === 'CRITERIA_FINALIZED' ? (
                    <Radio className="h-4 w-4 shrink-0 text-electric-mustard-600" />
                  ) : null}
                  {stateChip(session)}
                </div>
              </AppCard>
            );
          })}
        </div>
      )}
      <div className="mt-6">
        <BallLine
          ballWith={
            mayCreateSession
              ? 'you — open a session or start a new one'
              : mine.length > 0
                ? 'you — open a session you take part in'
                : SESSION_CREATE_BALL
          }
        />
      </div>
    </AppShell>
  );
}
